import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { getVideoById, getRelatedVideos, categories } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [video, setVideo] = useState<Video | undefined>();
  const [related, setRelated] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);
    if (id) {
      const found = getVideoById(id);
      setVideo(found);
      if (found) {
        setRelated(getRelatedVideos(found, 6));
      }
    }
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该视频可能已被移除或链接无效</p>
          <Link
            to="/"
            className="px-6 py-3 rounded-lg bg-primary-500 text-foreground-950 font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categories.find((c) => c.slug === video.category)?.name || video.category;
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${video.category}` },
    { label: video.title },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.thumbnail,
    datePublished: video.releaseDate,
    author: {
      '@type': 'Organization',
      name: '蓝莓视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '蓝莓视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.blueberryvideo.com/video/${video.id}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Video Player Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Player */}
                <div className="relative aspect-video rounded-lg overflow-hidden bg-background-900 mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 蓝莓视频高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer group">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center transition-transform group-hover:scale-110">
                      <i className="ri-play-fill text-foreground-950 text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                  {/* Controls Mock */}
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/80 to-transparent flex items-end px-4 pb-3">
                    <div className="w-full h-1 rounded-full bg-foreground-400/30">
                      <div className="w-1/3 h-full rounded-full bg-primary-500"></div>
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <article>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                    {video.title}
                  </h1>

                  {/* Meta Bar */}
                  <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views} 次播放
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-primary-500"></i>
                      {video.rating} 分
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {video.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {video.releaseDate}
                    </span>
                    <Link
                      to={`/category/${video.category}`}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-folder-line"></i>
                      {categoryName}
                    </Link>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-background-200 text-foreground-500 text-xs whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2">视频简介</h2>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {video.description}
                    </p>
                  </div>
                </article>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-20">
                  <h3 className="text-sm font-bold text-foreground-900 mb-4">相关推荐</h3>
                  <div className="space-y-4">
                    {related.slice(0, 5).map((v) => (
                      <Link
                        key={v.id}
                        to={`/video/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })}
                      >
                        <div className="w-28 h-16 shrink-0 rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-[11px] text-foreground-400">
                            <span>{v.views}</span>
                            <span className="text-primary-500">{v.rating}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>

            {/* Related Section */}
            {related.length > 0 && (
              <section className="pb-12" data-product-shop="">
                <h2 className="text-lg font-bold text-foreground-900 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {related.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}